import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import api from '../../api'
import { CompanyType } from '../../common/constants'
import MainContext from '../../common/MainContext'
import CreateButton from '../../components/CreateButton'
import Breadcrumb from '../../components/Breadcrumb'
import { ReactComponent as ExportIcon } from '../../assets/images/icons/ic-export.svg'
import Card from '../../components/cards/Card'
import { apiErrorText } from '../../utils/apiError'
import Button from '../../components/Button'
import MetricStatBlock from '../../components/analytics/MetricStatBlock'
import Dialog from '../../components/dialogs/Dialog'
import HeaderFooterLayout from '../../components/layouts/HeaderFooterLayout'
import MultipleSelect from '../../components/MultipleSelect'
import Pagination from '../../components/Pagination'
import Skeleton from '../../components/Skeleton'
import StudentProgress from '../../components/StudentProgress'
import Table from '../../components/Table'
import TextInput from '../../components/TextInput'
import typo from '../../typography.module.css'
import styles from './CompanyDashboard.module.css'

const getStudentViewedLessonsValue = (student) => {
  if (!student?.modules || !Array.isArray(student.modules)) {
    return 0
  }

  return student.modules.reduce((total, module) => {
    const completedLessons = Array.isArray(module?.lessons)
      ? module.lessons.filter((lesson) => lesson?.mandatory !== false && lesson?.completed).length
      : 0
    return total + completedLessons
  }, 0)
}

const getStudentTotalLessonsValue = (student) => {
  if (!student?.modules || !Array.isArray(student.modules)) {
    return 0
  }

  return student.modules.reduce((total, module) => {
    return total + (Array.isArray(module?.lessons) ? module.lessons.filter((lesson) => lesson?.mandatory !== false).length : 0)
  }, 0)
}

const getStudentProgressValue = (student) => {
  if (!student) {
    return 0
  }

  if (student.edition_type === 'live') {
    const duration = Number(student.edition_duration) || 0
    const progress = Number(student.edition_seconds_progress) || 0
    if (duration <= 0) {
      return 0
    }
    const percentage = Math.round((progress * 100) / duration)
    return Number.isNaN(percentage) ? 0 : Math.max(0, Math.min(100, percentage))
  }

  const totalLessons = getStudentTotalLessonsValue(student)
  const viewedLessons = getStudentViewedLessonsValue(student)
  if (totalLessons <= 0) {
    return 0
  }

  const percentage = Math.round((viewedLessons / totalLessons) * 100)
  return Number.isNaN(percentage) ? 0 : percentage
}

const CompanyCourseDashboard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { courseId } = useParams()
  const { user } = useContext(MainContext)

  const [courseDetail, setCourseDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [pageError, setPageError] = useState('')
  const [progresses, setProgresses] = useState([])
  const [loadingProgresses, setLoadingProgresses] = useState(false)
  const [progressesCount, setProgressesCount] = useState(0)
  const [progressesPage, setProgressesPage] = useState(0)
  const [progressesPer] = useState(25)
  const [studentsSearchQuery, setStudentsSearchQuery] = useState('')
  const [studentProgressDetail, setStudentProgressDetail] = useState(null)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false)
  const [assignableUsers, setAssignableUsers] = useState([])
  const [selectedUsersToEnroll, setSelectedUsersToEnroll] = useState([])
  const [loadingAssignableUsers, setLoadingAssignableUsers] = useState(false)
  const [assigningUsers, setAssigningUsers] = useState(false)
  const [enrollError, setEnrollError] = useState('')
  const [assignableSummary, setAssignableSummary] = useState({
    availableSlots: 0,
    maxUsers: 0,
    registeredUsers: 0,
    count: 0,
  })

  const isB2BTeaching = user?.company?.type === CompanyType.B2BTeaching

  const parsedCourseId = useMemo(() => {
    const value = Number(courseId)
    return Number.isFinite(value) ? value : null
  }, [courseId])

  const fallbackCourseName = useMemo(() => {
    return String(location?.state?.courseName || '').trim()
  }, [location?.state?.courseName])

  const loadCourseDetail = useCallback(async () => {
    if (!isB2BTeaching || !parsedCourseId) {
      return
    }

    setLoadingDetail(true)
    try {
      const response = await api.get(`/company/dashboard/courses/${parsedCourseId}`)
      setCourseDetail(response || null)
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
      setCourseDetail(null)
    }
    setLoadingDetail(false)
  }, [isB2BTeaching, parsedCourseId])

  const fetchProgresses = useCallback(async ({ page = progressesPage, q = studentsSearchQuery } = {}) => {
    if (!isB2BTeaching || !parsedCourseId) {
      return []
    }

    setPageError('')
    setLoadingProgresses(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        per: String(progressesPer),
        course_id: String(parsedCourseId),
      })

      if (q?.trim()) {
        params.set('q', q.trim())
      }

      const response = await api.get(`/company/dashboard/progresses?${params.toString()}`, { timeout: 0 })
      const items = response?.progresses || []
      setProgresses(items)
      setProgressesCount(response?.count || 0)
      return items
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
      setProgresses([])
      setProgressesCount(0)
      return []
    } finally {
      setLoadingProgresses(false)
    }
  }, [isB2BTeaching, parsedCourseId, progressesPage, progressesPer, studentsSearchQuery])

  useEffect(() => {
    loadCourseDetail()
  }, [loadCourseDetail])

  useEffect(() => {
    fetchProgresses({ page: progressesPage })
  }, [fetchProgresses, progressesPage, studentsSearchQuery])

  const onStudentsSearch = useCallback((value) => {
    setStudentsSearchQuery(String(value || ''))
    setProgressesPage(0)
  }, [])

  const loadAssignableUsers = useCallback(async () => {
    if (!isB2BTeaching || !parsedCourseId) {
      return
    }

    setLoadingAssignableUsers(true)
    setEnrollError('')
    try {
      const response = await api.get(`/company/dashboard/courses/${parsedCourseId}/assignable-users`, { timeout: 0 })
      const options = (response?.assignable_users || []).map((item) => ({
        id: item.id,
        name: `${item?.surname || ''} ${item?.name || ''}`.trim() + (item?.email ? ` (${item.email})` : ''),
        value: item.id,
        email: item?.email,
      }))

      setAssignableUsers(options)
      setSelectedUsersToEnroll([])
      setAssignableSummary({
        availableSlots: Number(response?.available_slots || 0),
        maxUsers: Number(response?.max_users || 0),
        registeredUsers: Number(response?.registered_users || 0),
        count: Number(response?.count || 0),
      })
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
      setAssignableUsers([])
      setAssignableSummary({
        availableSlots: 0,
        maxUsers: 0,
        registeredUsers: 0,
        count: 0,
      })
      setEnrollError(apiErrorText(e, 'Impossibile caricare gli utenti iscrivibili'))
    } finally {
      setLoadingAssignableUsers(false)
    }
  }, [isB2BTeaching, parsedCourseId])

  const openEnrollDialog = useCallback(async () => {
    setEnrollDialogOpen(true)
    await loadAssignableUsers()
  }, [loadAssignableUsers])

  const selectedUsersExceedSlots = useMemo(() => {
    return selectedUsersToEnroll.length > Number(assignableSummary.availableSlots || 0)
  }, [assignableSummary.availableSlots, selectedUsersToEnroll.length])

  const enrollStudents = useCallback(async () => {
    if (!parsedCourseId || assigningUsers || selectedUsersToEnroll.length === 0) {
      return
    }

    if (selectedUsersExceedSlots) {
      setEnrollError('Hai selezionato più utenti dei posti disponibili')
      return
    }

    setAssigningUsers(true)
    setEnrollError('')
    try {
      const payload = {
        user_ids: selectedUsersToEnroll.map((item) => item.id),
      }
      await api.post(`/company/dashboard/courses/${parsedCourseId}/enroll`, payload)

      setEnrollDialogOpen(false)
      setSelectedUsersToEnroll([])
      await Promise.all([
        loadCourseDetail(),
        fetchProgresses({ page: progressesPage }),
      ])
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
      await loadAssignableUsers()
      setEnrollError(apiErrorText(e, 'Impossibile iscrivere gli studenti selezionati'))
    } finally {
      setAssigningUsers(false)
    }
  }, [assigningUsers, fetchProgresses, loadAssignableUsers, loadCourseDetail, parsedCourseId, progressesPage, selectedUsersExceedSlots, selectedUsersToEnroll])

  const escapeCsvValue = useCallback((value) => {
    const normalized = String(value ?? '')
    if (/[,\n";]/.test(normalized)) {
      return `"${normalized.replace(/"/g, '""')}"`
    }
    return normalized
  }, [])

  const exportCsv = useCallback(async () => {
    if (!isB2BTeaching || !parsedCourseId || exportingCsv) {
      return
    }

    setExportingCsv(true)
    try {
      const per = 100
      let page = 0
      let total = null
      const allStudents = []

      while (total === null || allStudents.length < total) {
        const params = new URLSearchParams({
          page: String(page),
          per: String(per),
          course_id: String(parsedCourseId),
        })

        if (studentsSearchQuery.trim()) params.set('q', studentsSearchQuery.trim())

        const response = await api.get(`/company/dashboard/progresses?${params.toString()}`, { timeout: 0 })
        const items = Array.isArray(response?.progresses) ? response.progresses : []
        const count = Number(response?.count || 0)

        if (total === null) {
          total = count
        }

        allStudents.push(...items)

        if (!items.length) {
          break
        }

        page += 1
      }

      const header = [
        'Studente ID',
        'Nome',
        'Cognome',
        'Email',
        'Tipo edizione',
        'Modulo',
        'Lezione ID',
        'Lezione',
        'Tipo lezione',
        'Posizione',
        'Inizio lezione',
        'Fine lezione',
        'Vista il',
        'Completata',
        'Durata lezione (s)',
        'Durata video (s)',
        'Live fruita (s)',
        'Recorded fruita (s)',
        'Live %',
        'Recorded %',
        'Registrato il',
        'Ultimo accesso',
      ]

      const rows = allStudents.flatMap((student) => {
        const registeredAt = student?.registered_at ? new Date(student.registered_at).toLocaleString('it-IT') : ''
        const lastAccess = student?.last_access ? new Date(student.last_access).toLocaleString('it-IT') : ''
        const modules = Array.isArray(student?.modules) ? student.modules : []

        return modules.flatMap((module) => {
          const lessons = Array.isArray(module?.lessons) ? module.lessons : []
          return lessons.map((lesson) => {
            const lessonDuration = Number(lesson?.duration || 0)
            const lessonVideoDuration = Number(lesson?.video_duration || 0)
            const liveDuration = Number(lesson?.live_duration || 0)
            const recordedDuration = Number(lesson?.recorded_duration || 0)
            const livePercentage = lessonDuration > 0 ? Math.min(100, Math.round((liveDuration * 100) / lessonDuration)) : 0
            const recordedPercentage = lessonVideoDuration > 0 ? Math.min(100, Math.round((recordedDuration * 100) / lessonVideoDuration)) : 0
            const completed = Boolean(lesson?.completed) || livePercentage >= 80 || recordedPercentage >= 90

            return [
              student?.id || '',
              student?.name || '',
              student?.surname || '',
              student?.email || '',
              student?.edition_type || '',
              module?.name || '',
              lesson?.id || '',
              lesson?.name || '',
              lesson?.type || '',
              lesson?.position || '',
              lesson?.starts_at ? new Date(lesson.starts_at).toLocaleString('it-IT') : '',
              (lesson?.ended_at || lesson?.ends_at) ? new Date(lesson.ended_at || lesson.ends_at).toLocaleString('it-IT') : '',
              lesson?.viewed_at ? new Date(lesson.viewed_at).toLocaleString('it-IT') : '',
              completed ? 'true' : 'false',
              lessonDuration,
              lessonVideoDuration,
              liveDuration,
              recordedDuration,
              livePercentage,
              recordedPercentage,
              registeredAt,
              lastAccess,
            ]
          })
        })
      })

      const csvContent = [header, ...rows]
        .map((line) => line.map((cell) => escapeCsvValue(cell)).join(','))
        .join('\n')

      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      const safeCourseName = String(courseDetail?.course?.name || fallbackCourseName || `corso-${parsedCourseId}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

      link.href = url
      link.download = `progressi-${safeCourseName || `corso-${parsedCourseId}`}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
    } finally {
      setExportingCsv(false)
    }
  }, [courseDetail?.course?.name, escapeCsvValue, exportingCsv, fallbackCourseName, isB2BTeaching, parsedCourseId, studentsSearchQuery])

  const progressRows = useMemo(() => {
    return (progresses || []).map((student) => ({
      ...student,
      id: `${student.id}-${student.course_id ?? 'course'}`,
      course_name: student.course_name || 'N/A',
      lessons: {
        value: getStudentViewedLessonsValue(student),
        type: 'number',
        component: <div style={{ whiteSpace: 'nowrap' }}>{`${getStudentViewedLessonsValue(student)} / ${getStudentTotalLessonsValue(student)}`}</div>,
      },
      progress: {
        value: getStudentProgressValue(student),
        type: 'number',
        component: <div style={{ whiteSpace: 'nowrap' }}>{getStudentProgressValue(student)}%</div>,
      },
    }))
  }, [progresses])

  if (!isB2BTeaching) {
    return (
      <HeaderFooterLayout>
        <div className={styles.container}>
          <div className={styles.section}>
            <div className={styles.sectionInner}>
              <div className={typo.title}>Dettaglio Corso Aziendale</div>
              <div className={styles.subtitle}>Questa pagina e disponibile solo per aziende B2B Teaching.</div>
              <Button appearance='subtle' onClick={() => navigate('/')}>
                Torna alla dashboard
              </Button>
            </div>
          </div>
        </div>
      </HeaderFooterLayout>
    )
  }

  return (
    <HeaderFooterLayout>
      <div className={`${styles.container} ${styles.teaching}`}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{ label: 'Dashboard', to: '/' }, { label: courseDetail?.course?.name || fallbackCourseName || 'Dettaglio corso' }]} />
            <header className={styles.pageHeader}>
              <h1 className={styles.pageTitle}>{courseDetail?.course?.name || fallbackCourseName || 'Dettaglio corso'}</h1>
              <CreateButton permissionPage="dashboard" onClick={openEnrollDialog}>Iscrivi studenti</CreateButton>
            </header>

            {pageError && <div role="alert" className={styles.dialogError}>{pageError}</div>}
            <div className={styles.analyticsRow}>
              <MetricStatBlock
                title='ISCRITTI'
                actualValue={loadingDetail ? null : (courseDetail?.stats?.enrolled_users || 0)}
                loading={loadingDetail}
                containerClassName={styles.analyticCard}
                titleClassName={styles.analyticTitle}
                actualClassName={styles.actualAnalytic}
                previousClassName={styles.previousAnalytic}
                skeletonClassName={styles.analyticSkeleton}
              />
              <MetricStatBlock
                title='POSTI DISPONIBILI'
                actualValue={loadingDetail ? null : (courseDetail?.stats?.available_slots || 0)}
                loading={loadingDetail}
                containerClassName={styles.analyticCard}
                titleClassName={styles.analyticTitle}
                actualClassName={styles.actualAnalytic}
                previousClassName={styles.previousAnalytic}
                skeletonClassName={styles.analyticSkeleton}
              />
              <MetricStatBlock
                title='FREQUENZA ATTIVA'
                actualValue={loadingDetail ? null : `${courseDetail?.stats?.active_attendance_percentage || 0}%`}
                loading={loadingDetail}
                containerClassName={styles.analyticCard}
                titleClassName={styles.analyticTitle}
                actualClassName={styles.actualAnalytic}
                previousClassName={styles.previousAnalytic}
                skeletonClassName={styles.analyticSkeleton}
              />
              <MetricStatBlock
                title='COMPLETAMENTO >= 80%'
                actualValue={loadingDetail ? null : `${courseDetail?.stats?.completion_80_plus_percentage || 0}%`}
                loading={loadingDetail}
                containerClassName={styles.analyticCard}
                titleClassName={styles.analyticTitle}
                actualClassName={styles.actualAnalytic}
                previousClassName={styles.previousAnalytic}
                skeletonClassName={styles.analyticSkeleton}
              />
            </div>

            <Card appearance="modern" integratedTable title="Avanzamento studenti" actions={(<Button appearance='subtle' disabled={exportingCsv || loadingProgresses} onClick={exportCsv}>
                    <ExportIcon aria-hidden="true" style={{ width: 12, height: 12 }} />
                    {exportingCsv ? 'Esportazione...' : 'Esporta'}
                  </Button>)}>
              <div className={styles.filtersRow}>
                <div className={styles.filterSearch}>
                  <TextInput appearance="modern"
                    onKeyUp={onStudentsSearch}
                    type='search'
                    placeholder='Cerca utente'
                  />
                </div>
              </div>

              <Table
                integrated
                columns={[
                  { key: 'name', label: 'NOME', type: 'string' },
                  { key: 'surname', label: 'COGNOME', type: 'string' },
                  { key: 'email', label: 'EMAIL', type: 'string' },
                  { key: 'lessons', label: 'LEZIONI', type: 'custom' },
                  { key: 'progress', label: 'PROGRESSO', type: 'custom' },
                  { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
                  { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' },
                ]}
                loading={loadingProgresses}
                loadingN={6}
                onRowClick={(student) => setStudentProgressDetail(student)}
                defaultSort={{ key: 'surname', direction: 'asc' }}
                elements={progressRows}
              />

              {!loadingProgresses && !pageError && progresses.length === 0 && <p className={styles.emptyState}>Nessuno studente trovato. Prova a modificare la ricerca.</p>}
              <div className={styles.pagination}><Pagination
                page={progressesPage}
                total={progressesCount}
                per={progressesPer}
                onChange={setProgressesPage}
                loading={loadingProgresses}
              /></div>
            </Card>
          </div>
        </div>

        {studentProgressDetail && (
          <Dialog appearance="modern"
            title={`${studentProgressDetail.name} ${studentProgressDetail.surname}`}
            open={!!studentProgressDetail}
            onClose={() => setStudentProgressDetail(null)}
            style={{ width: '94vw', maxWidth: '1120px' }}
          >
            <StudentProgress modern student={studentProgressDetail} canSetLessonCompleted={false} />
          </Dialog>
        )}

        <Dialog appearance="modern"
          open={enrollDialogOpen}
          title='Iscrivi studenti'
          onClose={() => {
            if (!assigningUsers) {
              setEnrollDialogOpen(false)
              setEnrollError('')
            }
          }}
          style={{ width: '95vw', maxWidth: '640px' }}
          overflow='visible'
          action={(
            <Button
              style={{ borderRadius: 8 }}
              onClick={enrollStudents}
              disabled={assigningUsers || loadingAssignableUsers || selectedUsersToEnroll.length === 0 || selectedUsersExceedSlots}
            >
              {assigningUsers ? 'Iscrizione...' : 'Iscrivi studenti'}
            </Button>
          )}
        >
          <div className={styles.dialogBody}>
            <div className={styles.dialogSummaryGrid}>
              <div className={styles.dialogSummaryCard}>
                <div className={styles.dialogSummaryLabel}>POSTI DISPONIBILI</div>
                <div className={styles.dialogSummaryValue}>{loadingAssignableUsers ? <Skeleton width="70px" height={24} /> : assignableSummary.availableSlots}</div>
              </div>
              <div className={styles.dialogSummaryCard}>
                <div className={styles.dialogSummaryLabel}>ISCRITTI / LIMITE</div>
                <div className={styles.dialogSummaryValue}>{loadingAssignableUsers ? <Skeleton width="70px" height={24} /> : `${assignableSummary.registeredUsers} / ${assignableSummary.maxUsers}`}</div>
              </div>
              <div className={styles.dialogSummaryCard}>
                <div className={styles.dialogSummaryLabel}>UTENTI ISCRIVIBILI</div>
                <div className={styles.dialogSummaryValue}>{loadingAssignableUsers ? <Skeleton width="70px" height={24} /> : assignableSummary.count}</div>
              </div>
            </div>

            <div className={styles.dialogSelectWrap}>
              <div className={styles.dialogSelectLabel}>Seleziona utenti da iscrivere</div>
              <MultipleSelect appearance="modern" portal
                style={{ width: '100%' }}
                placeholder={loadingAssignableUsers ? 'Caricamento utenti...' : 'Seleziona utenti'}
                options={assignableUsers}
                selected={selectedUsersToEnroll}
                disabled={loadingAssignableUsers || assignableUsers.length === 0 || assigningUsers}
                onSelect={setSelectedUsersToEnroll}
                onSearch={() => { }}
                searchPlaceholder='Cerca utente'
              />
              <div className={styles.dialogSelectHint} aria-live="polite">Selezionati: {selectedUsersToEnroll.length}</div>
            </div>

            {!loadingAssignableUsers && !enrollError && assignableUsers.length === 0 && <p className={styles.dialogSelectHint}>Non ci sono studenti disponibili da iscrivere a questo corso.</p>}
            {selectedUsersExceedSlots && (
              <div role="alert" className={styles.dialogError}>
                Gli utenti selezionati superano i posti disponibili.
              </div>
            )}

            {enrollError && (
              <div role="alert" className={styles.dialogError}>{enrollError}</div>
            )}
          </div>
        </Dialog>
      </div>
    </HeaderFooterLayout>
  )
}

export default CompanyCourseDashboard
